import React, { useMemo } from 'react';
import {
  Checkbox,
  Field,
  Fieldset,
  Heading,
  Label,
  Radio,
  Select,
  ValidationMessage,
  Dialog,
} from '@innovationnorway/cl-react';
import SelectCountyTable from '../components/SelectCountyTable';
import AddEditButton from '../components/AddEditButton';
import useToggle from 'src/hooks/use-toggle';
import useGeonorge from 'src/features/one-way-in/hooks/use-geonorge';
import SelectedCountiesTable from '../components/SelectedCountiesTable';
import { CompanySizeCriteria } from 'src/api/v2';
import { Controller } from 'react-hook-form';
import useTargetPageOptions from '../hooks/use-target-page-options';
import useTargetForm, { TargetData } from '../hooks/subforms/use-target-form';
import { SubFormProps } from '../hooks/use-service-sub-form';
import { useTranslation } from 'react-i18next';
import { InnovationLevelCriteria } from 'src/api/v2';
import '../styles/TargetPage.scss';

const TargetPage = (props: SubFormProps<TargetData>) => {
  const { t: tServices } = useTranslation('services');
  const { isActive, activate, deactivate } = useToggle(false);
  const { countiesWithMunicipalities } = useGeonorge();
  const { form, validateIf, rules, registerWithRules } = useTargetForm(props);
  const { errors } = props;
  const { control, watch, getValues, setValue } = form;
  const { companyAgeOptions, companySizeOptions } = useTargetPageOptions(
    getValues('companyAge'),
    getValues('companySize'),
  );

  const counties = watch('counties');
  const municipalities = watch('municipalities');

  const selected = useMemo(() => {
    const selectedCounties = counties || [];
    const selectedMunicipalities = municipalities || [];

    return countiesWithMunicipalities
      .filter(
        (county) =>
          selectedCounties.includes(county.fylkesnavn) ||
          county.kommuner.some((municipality) => selectedMunicipalities.includes(municipality.kommunenummer)),
      )
      .map((county) => ({
        ...county,
        totalMunicipalityCount: county.kommuner.length,
        kommuner: county.kommuner.filter((municipality) =>
          selectedMunicipalities.includes(municipality.kommunenummer),
        ),
      }));
  }, [countiesWithMunicipalities, counties, municipalities]);

  return (
    <div>
      <Heading level={1}>{tServices('target.title')}</Heading>
      <div className="display--flex flex-direction--column gap--5 margin-top--2">
        <div>
          <Fieldset>
            <Fieldset.Legend data-size="md">{tServices('target.companyAge.label')}</Fieldset.Legend>
            <Fieldset.Description>{tServices('target.companyAge.help')}</Fieldset.Description>
            {companyAgeOptions.map((option) => (
              <Radio
                key={String(option.value)}
                label={option.label}
                value={option.value}
                defaultChecked={watch('companyAge') === option.value}
                {...registerWithRules('companyAge')}
              />
            ))}
            {(errors.companyAge?.message || form.formState.errors.companyAge?.message) && (
              <ValidationMessage>
                {errors.companyAge?.message || form.formState.errors.companyAge?.message}
              </ValidationMessage>
            )}
          </Fieldset>
        </div>
        <div>
          <Controller
            control={control}
            name="companySize"
            rules={rules.companySize}
            render={({ field, fieldState }) => (
              <Fieldset>
                <Fieldset.Legend data-size="md">{tServices('target.companySize.label')}</Fieldset.Legend>
                <Fieldset.Description>{tServices('target.companySize.help')}</Fieldset.Description>
                {companySizeOptions.map((option) => (
                  <Checkbox
                    key={String(option.value)}
                    label={option.label}
                    description={option.description}
                    value={String(option.value)}
                    checked={((field.value as CompanySizeCriteria[]) || []).includes(
                      option.value as CompanySizeCriteria,
                    )}
                    onChange={(e) => {
                      const { checked, value } = e.currentTarget;
                      const currentValues = (field.value as CompanySizeCriteria[]) || [];
                      if (checked) {
                        if (!currentValues.includes(value as CompanySizeCriteria)) {
                          setValue('companySize', [...currentValues, value as CompanySizeCriteria]);
                        }
                      } else {
                        setValue(
                          'companySize',
                          currentValues.filter((item) => item !== value),
                        );
                      }
                      form.trigger('companySize');
                    }}
                  />
                ))}
                {(fieldState.error || errors?.companySize) && (
                  <ValidationMessage>
                    {fieldState.error?.message || errors?.companySize?.message}
                  </ValidationMessage>
                )}
              </Fieldset>
            )}
          />
        </div>
        <Controller
          control={control}
          name="innovationLevel"
          rules={rules.innovationLevel}
          render={({ field, fieldState }) => (
            <Field>
              <Label>{tServices('target.innovationLevelCriteria.label')}</Label>
              <Field.Description data-size="sm">
                {tServices('target.innovationLevelCriteria.help')}
              </Field.Description>
              <Select
                name="innovationLevel"
                aria-invalid={!!(fieldState.error?.message || errors?.innovationLevel?.message)}
                value={field.value || ''}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  field.onChange((value || undefined) as InnovationLevelCriteria);
                  form.trigger('innovationLevel');
                }}
              >
                <Select.Option value="" disabled>
                  {tServices('target.innovationLevelCriteria.placeholder')}
                </Select.Option>
                {[
                  InnovationLevelCriteria.NotRelevant,
                  InnovationLevelCriteria.CompanyLevel,
                  InnovationLevelCriteria.RegionalLevel,
                  InnovationLevelCriteria.NationalLevel,
                  InnovationLevelCriteria.InternationalLevel,
                ].map((level) => (
                  <Select.Option key={level} value={level}>
                    {tServices(`target.innovationLevelCriteria.${level}`)}
                  </Select.Option>
                ))}
              </Select>
              {(fieldState.error?.message || errors?.innovationLevel?.message) && (
                <ValidationMessage>
                  {fieldState.error?.message || errors?.innovationLevel?.message}
                </ValidationMessage>
              )}
            </Field>
          )}
        />
        <div className="margin-bottom--5">
          <div className="margin-bottom--5">
            <Controller
              control={control}
              name="districtPolicyArea"
              rules={rules.districtPolicyArea}
              render={({ field, fieldState }) => (
                <Fieldset>
                  <Fieldset.Legend data-size="md">{tServices('target.districtPolicy.label')}</Fieldset.Legend>
                  <Fieldset.Description>{tServices('target.districtPolicy.help')}</Fieldset.Description>
                  <Radio
                    label={tServices('form.yes')}
                    name="districtPolicyArea"
                    value="true"
                    checked={field.value === true}
                    onChange={() => field.onChange(true)}
                  />
                  <Radio
                    label={tServices('form.no')}
                    name="districtPolicyArea"
                    value="false"
                    checked={field.value === false}
                    onChange={() => field.onChange(false)}
                  />
                  {(fieldState.error || errors.districtPolicyArea) && (
                    <ValidationMessage>
                      {fieldState.error?.message || errors.districtPolicyArea?.message}
                    </ValidationMessage>
                  )}
                </Fieldset>
              )}
            />
          </div>
          <div>
            <Field>
              <Label>{tServices('target.geographic.label')}</Label>
              <Field.Description data-size="sm">{tServices('target.geographic.help')}</Field.Description>
            </Field>
            <Controller
              control={control}
              name="counties"
              rules={validateIf(() => !isActive, rules.counties)}
              render={({ field, fieldState }) => (
                <>
                  <SelectedCountiesTable countiesWithMunicipalities={selected} />
                  <AddEditButton
                    hasSelection={selected.length > 0}
                    addLabel={tServices('form.add')}
                    editLabel={tServices('form.edit.button')}
                    onClick={() => {
                      activate();
                      field.onChange();
                    }}
                    className="margin-top--2 padding--2"
                  />
                  {fieldState.error && (
                    <ValidationMessage className="margin-top--2">
                      {fieldState.error.message}
                    </ValidationMessage>
                  )}
                </>
              )}
            />
          </div>
        </div>
        <Dialog
          open={isActive}
          onClose={() => deactivate()}
          title={tServices('target.geographic.dialogTitle')}
          className="county-selection-dialog"
        >
          <SelectCountyTable
            countiesWithMunicipalities={countiesWithMunicipalities}
            municipalities={municipalities || []}
            setValue={setValue}
            getValues={getValues}
            onClose={() => deactivate()}
          />
        </Dialog>
      </div>
    </div>
  );
};

export default TargetPage;
